import { z } from 'zod'
import { type RuleExecutionLog } from './rule-execution-logs-store'

type Parsed = RuleExecutionLog['parsed'][number]

const SCHEMA_LOG_INFO = z.object({
  ruleExecutionInfo: z.object({
    executionId: z.string(),
    uri: z.string(),
    executorClass: z.string(),
    label: z.string(),
    props: z.any(), // params from RuleExecutionInfo is not updated
    tags: z.array(z.string()),
  }),
  params: z.any(), // params that gets updated
})

/** a counter only to be used in parseLogInput, to ensure stable behaviour */
let logId = -1

export function parseLogInput(
  input: Omit<RuleExecutionLog, 'parsed' | 'id'>,
): RuleExecutionLog {
  logId++

  const messageItems = input.messages.map(item => {
    // `item.timestamp` should be ISO-format e.g. 1337-01-01T13:37.420
    const [before, after] = item.timestamp.slice(11).split('.')
    return {...item, timestamp: `${before}.${after.padStart(3, '0')}`}
  })

  const parsedItems: Parsed[] = []
  for (const item of messageItems) {
    try {
      const parsed = parseMessageInto(item)
      if (parsed) parsedItems.push(parsed)
    } catch(e) {
      console.error(e)
    }
  }

  return {
    ...input, id: logId,
    messages: messageItems,
    parsed: parsedItems,
  }
}

function parseMessageInto(
  item: RuleExecutionLog['messages'][number],
): null | Parsed {
    const [maybePrefix, maybeParsableJson] = item.message.split("\n\n")
    switch (maybePrefix) {

      case "/* PRE-RULE-EXECUTION */": {
        const rule = parseRuleExecutionInfo(maybeParsableJson)

        return {
          id: parsedId,
          type: 'before-rule',
          timestamp: item.timestamp,
          tag: rule.tag,
          uri: rule.uri,
          params: JSON.stringify(rule.params, null, 4),
        }
      }

      case "/* POST-RULE-EXECUTION */": {
        const rule = parseRuleExecutionInfo(maybeParsableJson)

        return {
          id: parsedId,
          type: 'after-rule',
          timestamp: item.timestamp,
          tag: rule.tag,
          uri: rule.uri,
          params: JSON.stringify(rule.params, null, 4),
        }
      }

      case "/* RULE-ERRORS */": {
        const obj = JSON.parse(maybeParsableJson)
        const parsed = z.object({
          ruleExecutionInfo: SCHEMA_LOG_INFO.shape.ruleExecutionInfo,
          errors: z.array(z.string()),
        }).parse(obj)

        parsedId++
        return {
          id: parsedId,
          type: 'rule-errors',
          timestamp: item.timestamp,
          tag: parsed.ruleExecutionInfo.tags[0],
          uri: parsed.ruleExecutionInfo.uri,
          params: '',
          errors: parsed.errors,
        }
      }

      default:
        return null

    }
}

/** a counter only to be used in parseRuleExecutionInfo, to ensure stable behaviour */
let parsedId = -1

function parseRuleExecutionInfo(str: string) {
  const obj = JSON.parse(str)
  const parsed = SCHEMA_LOG_INFO.parse(obj)
  if (parsed.ruleExecutionInfo.tags.length !== 1) console.error('expected only one tag')

  parsedId++

  return {
    id: parsedId,
    tag: parsed.ruleExecutionInfo.tags[0],
    uri: parsed.ruleExecutionInfo.uri,
    params: parsed.params,
  }
}
